import React, { useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { LinkItem } from '../types';
import { Check, Copy, ExternalLink, QrCode, Plus, Edit3, ArrowRight } from 'lucide-react';
import { copyToClipboard } from '../lib/utils';
import { useToast } from '../hooks/useToast';

interface LinkSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  link: LinkItem | null;
  onOpenQR: (shortCode: string, title: string) => void;
  onEdit: (id: string) => void;
  onCreateAnother: () => void;
}

export const LinkSuccessModal: React.FC<LinkSuccessModalProps> = ({
  isOpen,
  onClose,
  link,
  onOpenQR,
  onEdit,
  onCreateAnother,
}) => {
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);

  if (!link) return null;

  const shortUrl = `${window.location.origin}/${link.short_code}`;

  const handleCopy = async () => {
    const ok = await copyToClipboard(shortUrl);
    if (ok) {
      setCopied(true);
      showToast('Copied to clipboard', shortUrl, 'success');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Short Link Created Successfully!"
      description="Your marketing short URL is live on Cloudflare edge."
      maxWidth="lg"
    >
      <div className="space-y-6">
        {/* Short URL Box */}
        <div className="p-4 rounded-2xl bg-gradient-to-br from-orange-950/30 via-slate-900 to-slate-900 border border-orange-500/30 shadow-xl">
          <p className="text-xs font-semibold text-orange-400 uppercase tracking-wider mb-2">
            Branded Short URL
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <span className="text-base sm:text-lg font-mono font-bold text-white tracking-tight break-all">
              {shortUrl}
            </span>
            <Button
              size="sm"
              onClick={handleCopy}
              leftIcon={copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          </div>
        </div>

        {/* Destination Information */}
        <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 text-xs space-y-2">
          <div className="flex items-center justify-between text-slate-400">
            <span>Destination:</span>
            <a
              href={link.destination_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-400 hover:text-orange-300 flex items-center gap-1 font-mono truncate max-w-[280px]"
            >
              <span className="truncate">{link.destination_url}</span>
              <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          </div>
          <div className="flex items-center justify-between text-slate-400">
            <span>Link Title:</span>
            <span className="text-slate-200 font-medium">{link.title}</span>
          </div>
          {link.campaign_name && (
            <div className="flex items-center justify-between text-slate-400">
              <span>Campaign:</span>
              <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-medium">
                {link.campaign_name}
              </span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(shortUrl, '_blank')}
            leftIcon={<ExternalLink className="w-4 h-4" />}
          >
            Test Redirect
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onClose();
              onOpenQR(link.short_code, link.title);
            }}
            leftIcon={<QrCode className="w-4 h-4" />}
          >
            Get QR Code
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onClose();
              onEdit(link.id);
            }}
            leftIcon={<Edit3 className="w-4 h-4" />}
          >
            Edit Metadata
          </Button>
        </div>

        <div className="pt-2 border-t border-slate-800 flex justify-end">
          <Button
            variant="primary"
            onClick={() => {
              onClose();
              onCreateAnother();
            }}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Create Another Link
          </Button>
        </div>
      </div>
    </Modal>
  );
};
